
import { defineComponent, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import IncomingChargebacks from "@/components/widgets/mixed/IncomingChargebacks.vue";

interface Group {
  companygroupid: string;
  companygroupname: string;
  companyid: string;
}

interface Merchant {
  id: string;
  merchant_id: string;
  mid_number: string;
  dba_name: string;
  group_id: string;
}
export default defineComponent({
  name: "chargebacks-incoming",
  components: {
    Datatable,
    IncomingChargebacks,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const amount = ref("0");
    const count = ref(0);
    const monthAmount = ref("0");
    const monthCount = ref(0);
    const respondedCount = ref(0);
    const respondedAmount = ref("0");
    const unrespondedAmount = ref("0");
    const unrespondedCount = ref(0);
    const needInformation = ref(0);
    const labelPosition = ref("left");
    const size = ref("large");
    const usergroups = ref<Group[]>([]);
    const groupmids = ref<Merchant[]>([]);
    const parameters = ref<unknown>(null);
    const username = ref("");
    const alias_name = ref("");
    const password = ref("");
    const getProcsButton = ref<HTMLButtonElement | null>(null);
    const procs = ref([]);
    const processor_id = ref("");
    const searchParam = ref({
      search: "",
      id: "",
      merchant_id: "",
      case_number: "",
      mid_number: "",
      reason: "",
      chargeback_status: "",
      reason_condition: "",
      dispute_date: "",
      transaction_found: "",
    });

    onMounted(async () => {
      setCurrentPageTitle("Chargebacks Incoming");
      loading.value = true;

      const keys = Object.keys(route.query);
      if (keys.length > 0) {
        searchParam.value[keys[0]] = route.query[keys[0]];
      }

      setTimeout(async () => {
        await getGroups();

        await getAmounts();

        tableData.value = await getIncomings(pagination.value, sort.value);
        loading.value = false;
      }, 1000);
    });

    const formatCash = (n) => {
      if (n < 1e3) return n;
      if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + "K";
      if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + "M";
      if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(1) + "B";
      if (n >= 1e12) return +(n / 1e12).toFixed(1) + "T";
    };

    const getAmounts = async () => {
      let url;
      try {
        if (selectedGroupMid.value && selectedGroupMid.value.length === 1) {
          url = `chargebacks/chargebacks.groups?offset=0&limit=1000&group_id=${selectedGroupMid.value[0]}`;
        } else if (
          selectedGroupMid.value &&
          selectedGroupMid.value.length === 2
        ) {
          url = `chargebacks/chargebacks.merchants?offset=0&limit=1000&merchant_id=${selectedGroupMid.value[1]}`;
        } else {
          url = `chargebacks/chargebacks.groups?offset=0&limit=1000&group_id=IN${store.getters.currentUser.data.group_owners}`;
        }

        const { data } = await ApiService.get(url);
        amount.value = data.data.reduce(
          (partialSum, a) =>
            a.amount === null
              ? partialSum
              : partialSum + parseFloat(a.amount.replace(/,/g, "")),
          0
        );
        amount.value = formatCash(amount.value);

        count.value = data.data.reduce(
          (partialSum, a) =>
            a.count === null
              ? partialSum
              : partialSum + parseFloat(a.count.replace(/,/g, "")),
          0
        );
        count.value = formatCash(count.value);

        respondedAmount.value = data.data.reduce(
          (partialSum, a) =>
            a.responded_amount === null
              ? partialSum
              : partialSum + parseFloat(a.responded_amount.replace(/,/g, "")),
          0
        );
        respondedAmount.value = formatCash(respondedAmount.value);

        respondedCount.value = data.data.reduce(
          (partialSum, a) =>
            a.chargebacks_responded === null
              ? partialSum
              : partialSum +
                parseFloat(a.chargebacks_responded.replace(/,/g, "")),
          0
        );
        respondedCount.value = formatCash(respondedCount.value);

        unrespondedAmount.value = data.data.reduce(
          (partialSum, a) =>
            a.unresponded_amount === null
              ? partialSum
              : partialSum + parseFloat(a.unresponded_amount.replace(/,/g, "")),
          0
        );
        unrespondedAmount.value = formatCash(unrespondedAmount.value);

        unrespondedCount.value = data.data.reduce(
          (partialSum, a) =>
            a.chargebacks_unresponded === null
              ? partialSum
              : partialSum +
                parseFloat(a.chargebacks_unresponded.replace(/,/g, "")),
          0
        );
        unrespondedCount.value = formatCash(unrespondedCount.value);

        needInformation.value = data.data.reduce(
          (partialSum, a) =>
            a.need_information === null
              ? partialSum
              : partialSum + parseFloat(a.need_information.replace(/,/g, "")),
          0
        );
      } catch (e) {
        amount.value = "0";
        count.value = 0;
        respondedAmount.value = "0";
        respondedCount.value = 0;
        unrespondedAmount.value = "0";
        unrespondedCount.value = 0;
        needInformation.value = 0;
      }

      try {
        url = `chargebacks/month?offset=0&limit=1000`;
        if (selectedGroupMid.value && selectedGroupMid.value.length === 1) {
          url = `${url}&group_id=${selectedGroupMid.value[0]}`;
        } else if (
          selectedGroupMid.value &&
          selectedGroupMid.value.length === 2
        ) {
          url = `chargebacks/month.merchant?offset=0&limit=1000&merchant_id=${selectedGroupMid.value[1]}`;
        } else {
          url = `${url}&group_id=IN${store.getters.currentUser.data.group_owners}`;
        }

        const { data } = await ApiService.get(url);
        monthAmount.value = data.data[0].amount;
        monthAmount.value = formatCash(
          parseFloat(monthAmount.value.replace(/,/g, ""))
        );

        monthCount.value = data.data[0].count;
        monthCount.value = formatCash(monthCount.value);
      } catch (e) {
        monthAmount.value = "0";
        monthCount.value = 0;
      }
    };

    const selectedGroupMid = ref([]);
    const groupMidOptions = ref<unknown[]>([]);

    const getGroups = async () => {
      try {
        const { data } = await ApiService.get(
          `users/groups?offset=0&limit=1000&companygroupid=IN${store.getters.currentUser.data.group_owners}`
        );
        if (data.success) {
          usergroups.value = [...data.data];
        }
        const res = await ApiService.get(
          `merchants/accounts?offset=0&limit=1000&group_id=IN${store.getters.currentUser.data.group_owners}`
        );
        if (res.data.success) {
          groupmids.value = [...res.data.data];
        }
        groupMidOptions.value = usergroups.value.map((group) => {
          const groupMids = groupmids.value.filter(
            (mid) => mid.group_id === group.companygroupid
          );
          const mids = groupMids.map((mid) => {
            return {
              value: mid.merchant_id,
              label: mid.dba_name,
            };
          });
          return {
            value: group.companygroupid,
            label: group.companygroupname,
            children: mids,
          };
        });
      } catch (e) {
        groupMidOptions.value = [];
      }
    };
    const selGroupMid = async () => {
      loading.value = true;
      await getAmounts();
      tableData.value = await getIncomings(pagination.value, sort.value);
      loading.value = false;
    };

    const search = async () => {
      loading.value = true;
      tableData.value = await getIncomings(pagination.value, sort.value);
      loading.value = false;
    };

    const bulkActionPopulate = async () => {
      if (checkedIncomings.value.length > 0) {
        await bulkChargebackPopulate(checkedIncomings.value);
      }
      checkedIncomings.value = [];
    };
    const bulkChargebackPopulate = async (cbs) => {
      loading.value = true;

      try {
        const res = await ApiService.post(
          "chargebacks/incoming?action=bulk_populate",
          cbs
        );
        if (res.data.success) {
          Swal.fire({
            text: "Chargebacks Populated!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
          tableData.value = await getIncomings(pagination.value, sort.value);
          loading.value = false;
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };
    const bulkActionArchive = async () => {
      if (checkedIncomings.value.length > 0) {
        await bulkChargebackArchive(checkedIncomings.value);
      }
      checkedIncomings.value = [];
    };
    const bulkChargebackArchive = async (cbs) => {
      // eslint-disable-next-line
      const chargebackIds = cbs.join();
      loading.value = true;

      try {
        const res = await ApiService.put(
          `chargebacks/incoming?action=bulk_archive&ids=[${chargebackIds}]`,
          {}
        );
        if (res.data.success) {
          Swal.fire({
            text: "Chargebacks Archived!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
          tableData.value = await getIncomings(pagination.value, sort.value);
          loading.value = false;
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };
    const bulkActionResponse = async () => {
      if (checkedIncomings.value.length > 0) {
        await bulkResponseCreate(checkedIncomings.value);
      }
      checkedIncomings.value = [];
    };
    const bulkResponseCreate = async (cbs) => {
      // eslint-disable-next-line
      const chargebackIds = cbs.join();
      loading.value = true;

      try {
        const res = await ApiService.put(
          `chargebacks/incoming?action=response_packet&ids=[${chargebackIds}]&bulk=true`,
          {}
        );
        if (res.data.success) {
          Swal.fire({
            text: "Responses Created!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
          tableData.value = await getIncomings(pagination.value, sort.value);
          loading.value = false;
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };
    const bulkActionReset = async () => {
      if (checkedIncomings.value.length > 0) {
        await bulkResetChargebacks(checkedIncomings.value);
      }
      checkedIncomings.value = [];
    };
    const bulkResetChargebacks = async (cbs) => {
      // eslint-disable-next-line
      const chargebackIds = cbs.join();
      loading.value = true;

      try {
        const res = await ApiService.put(
          `chargebacks/incoming?action=reset&ids=[${chargebackIds}]&bulk=true`,
          {}
        );
        if (res.data.success) {
          Swal.fire({
            text: "Chargebacks Reset!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
          tableData.value = await getIncomings(pagination.value, sort.value);
          loading.value = false;
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };


    const getIncomings = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "&sort=id,desc";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }

        let url = `chargebacks/incoming?is_archived=0&is_responded=0&offset=${offset}&limit=${rowsPerPage}${sortParam}`;

        if (searchParam.value.search !== "") {
          url = `${url}&search[]=${searchParam.value.search}`;
        }
        if (searchParam.value.id !== "") {
          url = `${url}&id=${searchParam.value.id}`;
        }
        if (searchParam.value.merchant_id !== "") {
          url = `${url}&merchant_id=${searchParam.value.merchant_id}`;
        }
        if (searchParam.value.case_number !== "") {
          url = `${url}&case_number=${searchParam.value.case_number}`;
        }
        if (searchParam.value.mid_number !== "") {
          url = `${url}&mid_number=${searchParam.value.mid_number}`;
        }
        if (searchParam.value.reason !== "") {
          url = `${url}&reason=${searchParam.value.reason}`;
        }
        if (searchParam.value.chargeback_status !== "") {
          url = `${url}&chargeback_status=${searchParam.value.chargeback_status}`;
        }
        if (searchParam.value.reason_condition !== "") {
          url = `${url}&reason_condition=${searchParam.value.reason_condition}`;
        }
        if (searchParam.value.dispute_date !== "") {
          url = `${url}&dispute_date=${searchParam.value.dispute_date}`;
        }
        if (searchParam.value.transaction_found !== "") {
          url = `${url}&transaction_found=${searchParam.value.transaction_found}`;
        }

        if (selectedGroupMid.value && selectedGroupMid.value.length === 1) {
          url = `${url}&group_id=${selectedGroupMid.value[0]}`;
        } else if (
          selectedGroupMid.value &&
          selectedGroupMid.value.length === 2
        ) {
          url = `${url}&merchant_id=${selectedGroupMid.value[1]}`;
        } else {
          url = `${url}&group_id=IN${store.getters.currentUser.data.group_owners}`;
        }

        const { data } = await ApiService.get(url);
        return data;
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const pagination = ref({
      page: 1,
      rowsPerPage: 10,
    });
    const sort = ref({
      columnName: "",
      order: "",
    });
    const checkedIncomings = ref([]);
    const tableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "id",
        sortable: true,
      },
      {
        name: "Case Number",
        key: "case_number",
        sortable: true,
      },
      {
        name: "Mid Number",
        key: "mid_number",
        sortable: true,
      },
      {
        name: "Reason",
        key: "reason",
        sortable: true,
      },
      {
        name: "Status",
        key: "chargeback_status",
        sortable: true,
      },
      {
        name: "Date",
        key: "dispute_date",
        sortable: true,
      },
      {
        name: "Amount",
        key: "dispute_amount",
        sortable: true,
      },
      {
        name: "Populated",
        key: "transaction_found",
        sortable: true,
      },

      {
        name: "Case Details",
        key: "details",
      },
    ]);

    const tableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const loading = ref(true);

    const onCurrentChange = async (val) => {
      pagination.value.page = val;

      loading.value = true;
      tableData.value = await getIncomings(pagination.value, sort.value);
      loading.value = false;
    };

    const onSort = async (val) => {
      sort.value = val;

      loading.value = true;
      tableData.value = await getIncomings(pagination.value, sort.value);
      loading.value = false;
    };

    const onItemsPerPageChange = async (val) => {
      pagination.value.rowsPerPage = val;

      loading.value = true;
      tableData.value = await getIncomings(pagination.value, sort.value);
      loading.value = false;
    };

    const deleteCustomer = (id) => {
      console.log(id);
    };

    return {
      amount,
      count,
      monthAmount,
      monthCount,
      labelPosition,
      size,
      groupMidOptions,
      selGroupMid,
      selectedGroupMid,
      parameters,
      username,
      password,
      alias_name,
      getProcsButton,
      procs,
      processor_id,
      respondedAmount,
      respondedCount,
      unrespondedAmount,
      unrespondedCount,
      needInformation,
      searchParam,
      tableData,
      tableHeader,
      loading,
      checkedIncomings,
      pagination,
      bulkActionPopulate,
      bulkActionArchive,
      bulkActionResponse,
      bulkActionReset,
      onCurrentChange,
      onSort,
      onItemsPerPageChange,
      deleteCustomer,
      search,
    };
  },
});
